import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { generate, type ToVariants } from '@/components/experiments/Core/generateBasicExperimentComponent';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { useExperimentStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { MaybeRefOrGetter, toValue, useMounted } from '@vueuse/core';
import { computed } from 'vue';

// ?preferred_variant=APPLE_AMZ_CHANNEL_EXP:control
// ?preferred_variant=APPLE_AMZ_CHANNEL_EXP:variant_1
// ?preferred_variant=APPLE_AMZ_CHANNEL_EXP:variant_2
// ?preferred_variant=APPLE_AMZ_CHANNEL_EXP:variant_3

// ?remove_preferred_experiment=APPLE_AMZ_CHANNEL_EXP

export const AppleAmzChannelExpName = 'APPLE_AMZ_CHANNEL_EXP' as const;

export type AppleAmzChannelExpVariants = ToVariants<typeof AppleAmzChannelExpVariants>;

export const AppleAmzChannelExpVariants = {
	CONTROL: 'control',
	VARIANT_1: 'variant_1',
	VARIANT_2: 'variant_2',
	VARIANT_3: 'variant_3',
} as const;

export default generate(AppleAmzChannelExpName, AppleAmzChannelExpVariants);

export function useAppleAmzChannelExp(title: MaybeRefOrGetter<TitleDetail>) {
	const isMounted = useMounted();
	const { country } = useLanguageStore();
	const { isArbitrating } = useArbitrage();
	const { isLoggedIn } = useUserStore();
	const { activeVariantsWithControlGroup } = useExperimentStore();

	const appleAmzChannelExpVariant = computed(() => {
		if (
			!isMounted.value ||
			!['US', 'ES', 'GB'].includes(country.value) ||
			isLoggedIn.value ||
			isArbitrating.value ||
			!toValue(title).offers.some(offer => ['atp', 'avl'].includes(offer.package.shortName))
		)
			return null;

		return activeVariantsWithControlGroup.value[AppleAmzChannelExpName] as AppleAmzChannelExpVariants;
	});

	function getSortValueFromShortName(offer: TitleOfferFragment) {
		if (offer.package.shortName === 'avl') return 0;
		if (offer.package.shortName === 'atp') return 2;
		return 1;
	}

	function sortAndFilterAppleAmzChannelExpOffers(offers: TitleOfferFragment[]) {
		if (
			appleAmzChannelExpVariant.value === 'control' ||
			!appleAmzChannelExpVariant.value ||
			!offers.some(offer => ['atp', 'avl'].includes(offer.package.shortName))
		)
			return offers;

		return offers
			.slice()
			.filter(offer => offer.package.shortName !== 'atp' || appleAmzChannelExpVariant.value === 'variant_2')
			.sort((a, b) => getSortValueFromShortName(a) - getSortValueFromShortName(b));
	}

	function setAffiliateParamsAppleAmzExp(url: ClickoutUrl, offerType: 'ft' | 'ro', shortName: string) {
		const provider = shortName === 'atp' ? 'apple' : shortName == 'avl' ? 'amzapplchl' : 'amz';
		const countryLower = country.value.toLowerCase();
		const variant =
			appleAmzChannelExpVariant.value === 'control'
				? 'ctrl'
				: appleAmzChannelExpVariant.value?.replace('ariant_', '');

		const baseParam = `${provider}-${countryLower}-${offerType}-chlexp-${variant}`;

		if (shortName === 'atp') {
			return url.set('uct_ct', baseParam);
		}

		const number = country.value === 'US' ? '20' : '21';

		return url.setOnOffer('tag', `${baseParam}-${number}`);
	}

	return {
		appleAmzChannelExpVariant,
		sortAndFilterAppleAmzChannelExpOffers,
		setAffiliateParamsAppleAmzExp,
	};
}
